import { useCallback, useEffect, useId, useState } from 'react';
import { AGENT_CODES, PROVIDER_LABEL, RECOMMENDED_TIER, type AgentCode, type ProviderCode } from '@ambec/shared';

/** Redactores de capítulo de la DIA (generación de documentos): A2/A3 pilotos + A7–A18. */
const CAP_REDACTORES = new Set(['A2', 'A3', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12', 'A13', 'A14', 'A15', 'A16', 'A17', 'A18']);
import { ApiError, apiFetch } from '../../../lib/api';
import { agente } from '../../../lib/agentes';
import { inputClass, labelClass, type AgentConfig, type LlmProvider } from './tipos';
import { Modal } from '../../../components/Modal';

/**
 * Asignación de cerebro por agente: cada agente (Nicanor…Pablo) elige un CONECTOR
 * primario y uno de respaldo — el conector ya trae proveedor + modelo + key desde
 * Conectores LLM. El oficio NO se edita aquí: se entrena versionado en la app privada
 * (Newsletter → Entrenamiento). Guardrail N3≠N4 validado en backend y avisado inline.
 */

function traducirError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

interface Borrador {
  connector_id: string;
  fallback_connector_id: string;
  temperature: string;
  max_tokens: string;
}

function aBorrador(c: AgentConfig): Borrador {
  return {
    connector_id: c.connector_id ?? '',
    fallback_connector_id: c.fallback_connector_id ?? '',
    temperature: c.temperature != null ? String(c.temperature) : '',
    max_tokens: c.max_tokens != null ? String(c.max_tokens) : '',
  };
}

const etiquetaConector = (p: LlmProvider) => `${p.name} — ${p.model ?? 'sin modelo'}`;

/** Opciones agrupadas por proveedor (Anthropic, Gemini, OpenAI…): la casa es multi-LLM. */
function OpcionesConectores({ conectores, excluir }: { conectores: LlmProvider[]; excluir?: string }) {
  const grupos = new Map<string, LlmProvider[]>();
  for (const p of conectores) {
    if (p.id === excluir) continue;
    const label = p.code in PROVIDER_LABEL ? PROVIDER_LABEL[p.code as ProviderCode] : p.code;
    grupos.set(label, [...(grupos.get(label) ?? []), p]);
  }
  return (
    <>
      {[...grupos.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([label, ps]) => (
        <optgroup key={label} label={label}>
          {ps.map((p) => (
            <option key={p.id} value={p.id} disabled={!p.enabled}>
              {etiquetaConector(p)}{p.enabled ? '' : ' (deshabilitado)'}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

function TarjetaAgente({
  codigo,
  config,
  conectores,
  modeloDeN3,
  modeloDeN4,
  onGuardado,
}: {
  codigo: AgentCode;
  config: AgentConfig;
  conectores: LlmProvider[];
  modeloDeN3: string | null;
  modeloDeN4: string | null;
  onGuardado: () => void;
}) {
  const [b, setB] = useState<Borrador>(() => aBorrador(config));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const provId = useId();
  const fbId = useId();
  const tempId = useId();
  const maxId = useId();

  const id = agente(codigo);
  const nombre = config.display_name ?? id.nombre;
  const oficio = config.oficio ?? id.oficio;
  const tier = RECOMMENDED_TIER[codigo];

  const porId = new Map(conectores.map((p) => [p.id, p]));
  const primario = porId.get(b.connector_id) ?? null;
  const respaldo = porId.get(b.fallback_connector_id) ?? null;

  // Aviso preventivo del guardrail (el backend valida igual al guardar).
  const modeloContrario = codigo === 'N4' ? modeloDeN3 : codigo === 'N3' ? modeloDeN4 : null;
  const choqueGuardrail =
    !!modeloContrario &&
    !!primario?.model &&
    (primario.model === modeloContrario || (codigo === 'N4' && respaldo?.model === modeloContrario));

  function set<K extends keyof Borrador>(campo: K, valor: string) {
    setB((prev) => ({ ...prev, [campo]: valor }));
    setOk(false);
    setError(null);
  }

  async function guardar() {
    setError(null);
    setAviso(null);
    setOk(false);
    setGuardando(true);
    try {
      const body: Record<string, unknown> = {
        connector_id: b.connector_id || null,
        fallback_connector_id: b.fallback_connector_id || null,
      };
      if (b.temperature.trim() !== '') body.temperature = Number(b.temperature);
      body.max_tokens = b.max_tokens.trim() === '' ? null : Number(b.max_tokens);
      const res = await apiFetch<{ config: AgentConfig; warning?: string }>(
        `/api/admin/agent-configs/${codigo}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
      setB(aBorrador(res.config));
      if (res.warning) setAviso(res.warning);
      setOk(true);
      onGuardado();
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <section className="tarjeta p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[13px] font-semibold ${id.tinte}`}>
            {codigo}
          </span>
          <div>
            <h3 className="text-[15px] font-medium leading-tight text-azul-noche">
              {nombre}, {oficio}
            </h3>
            <p className="text-xs font-normal leading-tight text-gris">
              {codigo} · cerebro actual: {primario?.model ?? config.model ?? 'sin asignar'}
            </p>
          </div>
        </div>
        {tier && <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">Recomendado: {tier}</span>}
      </div>

      {error && <p role="alert" className="alerta-error mt-4">{error}</p>}
      {choqueGuardrail && !error && (
        <p role="status" className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs font-normal text-amber-800 ring-1 ring-inset ring-amber-200">
          Guardrail: Gabriela (N4) no puede auditar con el mismo modelo que redacta Violeta (N3) — {modeloContrario}. Elige un conector con otro modelo.
        </p>
      )}
      {ok && !aviso && !error && (
        <p role="status" className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-normal text-emerald-800 ring-1 ring-inset ring-emerald-200">
          Asignación guardada.
        </p>
      )}
      {aviso && ok && (
        <p role="status" className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs font-normal text-amber-800 ring-1 ring-inset ring-amber-200">
          Guardado. {aviso}
        </p>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={provId} className={labelClass}>Conector (cerebro)</label>
          <select id={provId} value={b.connector_id} onChange={(e) => set('connector_id', e.target.value)} className={inputClass}>
            <option value="">Sin asignar</option>
            <OpcionesConectores conectores={conectores} />
          </select>
          <p className="mt-1 text-[11px] font-normal text-gris">
            El conector define proveedor, modelo y credencial (Conectores LLM).
          </p>
        </div>

        <div>
          <label htmlFor={fbId} className={labelClass}>Conector de respaldo</label>
          <select id={fbId} value={b.fallback_connector_id} onChange={(e) => set('fallback_connector_id', e.target.value)} className={inputClass}>
            <option value="">Ninguno</option>
            <OpcionesConectores conectores={conectores} excluir={b.connector_id} />
          </select>
          <p className="mt-1 text-[11px] font-normal text-gris">
            Si el primario falla, la corrida reintenta una vez con este.
          </p>
        </div>

        <div>
          <label htmlFor={tempId} className={labelClass}>Temperatura</label>
          <input
            id={tempId}
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={b.temperature}
            onChange={(e) => set('temperature', e.target.value)}
            placeholder="0.0 – 2.0"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor={maxId} className={labelClass}>Máx. tokens de salida</label>
          <input
            id={maxId}
            type="number"
            min="1"
            step="1"
            value={b.max_tokens}
            onChange={(e) => set('max_tokens', e.target.value)}
            placeholder="Ej: 4096"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="text-[11px] font-normal leading-relaxed text-gris">
          El conocimiento de {nombre} (oficio y contrato) se entrena con versiones y pruebas en
          la app privada: Newsletter → Entrenamiento.
        </p>
        <button type="button" onClick={() => void guardar()} disabled={guardando} className="btn-primario">
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </section>
  );
}

export default function SeccionAgentes() {
  const [configs, setConfigs] = useState<AgentConfig[]>([]);
  const [conectores, setConectores] = useState<LlmProvider[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const [cfg, prov] = await Promise.all([
        apiFetch<{ configs: AgentConfig[] }>('/api/admin/agent-configs'),
        apiFetch<{ providers: LlmProvider[] }>('/api/admin/llm-providers'),
      ]);
      setConfigs(cfg.configs);
      setConectores(prov.providers);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (cargando) {
    return (
      <div role="status" aria-live="polite" className="tarjeta px-6 py-16 text-center text-sm font-normal text-gris">
        Cargando asignación de agentes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="tarjeta px-6 py-16 text-center">
        <p className="text-sm font-normal text-red-700">{error}</p>
        <button type="button" onClick={() => void cargar()} className="btn-secundario mt-4">
          Reintentar
        </button>
      </div>
    );
  }

  const porCodigo = new Map(configs.map((c) => [c.agent_code, c]));
  const porId = new Map(conectores.map((p) => [p.id, p]));
  const modeloEfectivo = (code: AgentCode): string | null => {
    const c = porCodigo.get(code);
    if (!c) return null;
    return (c.connector_id ? porId.get(c.connector_id)?.model : null) ?? c.model;
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm font-normal text-gris">
        Asigna el cerebro de cada agente eligiendo un conector de Conectores LLM — la casa es
        multi-LLM: Gemini, Claude, OpenAI o compatibles, según lo que conectes. Regla de la
        casa: Gabriela (N4) nunca audita con el mismo modelo con que redacta Violeta (N3).
      </p>

      {/* Víctor, el Director: orquestador determinista — no usa LLM, no se asigna */}
      <section className="tarjeta p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-azul-noche text-[13px] font-semibold text-white">
              V
            </span>
            <div>
              <h3 className="text-[15px] font-medium leading-tight text-azul-noche">Víctor, el Director</h3>
              <p className="text-xs font-normal leading-tight text-gris">orquestador · cerebro: código determinista</p>
            </div>
          </div>
          <span className="chip bg-slate-100 text-slate-700 ring-slate-300">Sin LLM · no se asigna</span>
        </div>
        <p className="mt-3 text-[13px] font-normal leading-relaxed text-texto">
          Víctor no consume ningún conector: descarga las fuentes con herramientas, comanda la
          cadena N1→N5 con los cerebros asignados abajo, aplica reparaciones y reporta cada
          etapa en vivo. Su trabajo se audita en cada corrida.
        </p>
      </section>

      {conectores.filter((p) => p.enabled).length === 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-normal text-amber-800 ring-1 ring-inset ring-amber-200">
          No hay conectores habilitados: crea uno en la pestaña Conectores LLM antes de asignar.
        </p>
      )}
      {/* Cards por agente agrupadas por línea de producto (la casa tendrá cada vez más).
          Los REDACTORES DE CAPÍTULO (DIA) son la sección para elegir el LLM primario y de
          respaldo con que se GENERAN los documentos (los 14 capítulos de la DIA). */}
      {([
        { titulo: 'Newsletter Regulatorio', pred: (c: string) => c.startsWith('N') },
        { titulo: 'Redactores de capítulos (DIA) — generación de documentos', pred: (c: string) => CAP_REDACTORES.has(c) },
        { titulo: 'Asistentes DIA', pred: (c: string) => c.startsWith('A') && !CAP_REDACTORES.has(c) },
        { titulo: 'Otros agentes', pred: (c: string) => !c.startsWith('N') && !c.startsWith('A') },
      ] as const).map((grupo) => {
        const codigos = [...new Set([...AGENT_CODES, ...configs.map((c) => c.agent_code)])].filter(grupo.pred);
        if (codigos.length === 0) return null;
        return (
      <section key={grupo.titulo}>
      <p className="mb-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-gris">{grupo.titulo}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {codigos.map((codigo) => {
          const cfg = porCodigo.get(codigo);
          const id = agente(codigo as AgentCode);
          const modelo = (cfg?.connector_id ? porId.get(cfg.connector_id)?.model : null) ?? cfg?.model ?? null;
          const fallback = cfg?.fallback_connector_id ? porId.get(cfg.fallback_connector_id)?.model : cfg?.fallback_model;
          return (
            <button
              key={codigo}
              type="button"
              onClick={() => setAbierto(codigo)}
              className="group flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors duration-150 hover:border-azul-profundo/40 hover:bg-celeste/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
            >
              <span className="flex items-center gap-3">
                <span aria-hidden="true" className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[12px] font-semibold ${id.tinte}`}>
                  {codigo}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-medium leading-tight text-azul-noche">{cfg?.display_name ?? id.nombre}</span>
                  <span className="block truncate text-[11px] font-normal leading-tight text-gris">{cfg?.oficio ?? id.oficio}</span>
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                {modelo ? (
                  <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">{modelo}</span>
                ) : (
                  <span className="chip bg-amber-50 text-amber-700 ring-amber-300">Sin cerebro asignado</span>
                )}
                {fallback && <span className="chip bg-slate-100 text-slate-600 ring-slate-300">resp: {fallback}</span>}
              </span>
              <span className="text-[11px] font-medium text-azul-profundo opacity-0 transition-opacity duration-150 group-hover:opacity-100">Configurar →</span>
            </button>
          );
        })}
      </div>
      </section>
        );
      })}

      {abierto && (
        <Modal titulo={`Configurar a ${porCodigo.get(abierto as AgentCode)?.display_name ?? agente(abierto as AgentCode).nombre} (${abierto})`} onCerrar={() => setAbierto(null)} ancho="xl">
          <TarjetaAgente
            codigo={abierto as AgentCode}
            config={(porCodigo.get(abierto as AgentCode) ?? {
              agent_code: abierto,
              display_name: null, oficio: null, connector_id: null, fallback_connector_id: null,
              provider_code: null, model: null, temperature: null, max_tokens: null,
              context_brief: null, fallback_provider_code: null, fallback_model: null,
            }) as AgentConfig}
            conectores={conectores}
            modeloDeN3={modeloEfectivo('N3')}
            modeloDeN4={modeloEfectivo('N4')}
            onGuardado={() => { setAbierto(null); void cargar(); }}
          />
        </Modal>
      )}
    </div>
  );
}
